if (!customElements.get('cart-drawer-product-slider')) {
  class CartDrawerProductSlider extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.visibilityObserver = null;

      this.handlePointerOver =
        this.handlePointerOver.bind(this);

      this.handleFocusIn =
        this.handleFocusIn.bind(this);
    }

    connectedCallback() {
      this.addEventListener(
        'pointerover',
        this.handlePointerOver
      );

      this.addEventListener(
        'focusin',
        this.handleFocusIn
      );

      /*
       * Drawer açılma dosyasına ihtiyaç duyulmaz.
       * Slider ekranda görünür olduğunda çalışır.
       */
      this.observeVisibility();

      if (window.Shopify?.designMode) {
        this.sectionLoadHandler = event => {
          const section = event.target;

          if (
            section === this ||
            section.contains(this) ||
            this.contains(section)
          ) {
            this.destroySlider();
            this.observeVisibility();
          }
        };

        document.addEventListener(
          'shopify:section:load',
          this.sectionLoadHandler
        );
      }
    }

    disconnectedCallback() {
      this.removeEventListener(
        'pointerover',
        this.handlePointerOver
      );

      this.removeEventListener(
        'focusin',
        this.handleFocusIn
      );

      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
      }

      if (
        window.Shopify?.designMode &&
        this.sectionLoadHandler
      ) {
        document.removeEventListener(
          'shopify:section:load',
          this.sectionLoadHandler
        );
      }

      this.destroySlider();
    }

    observeVisibility() {
      if (this.slider || this.visibilityObserver) return;

      this.visibilityObserver =
        new IntersectionObserver(
          entries => {
            const isVisible = entries.some(
              entry =>
                entry.isIntersecting &&
                entry.intersectionRatio > 0
            );

            if (!isVisible) return;

            this.visibilityObserver.disconnect();
            this.visibilityObserver = null;

            /*
             * Drawer animasyonu başladıktan sonra Swiper'ın
             * doğru genişliği hesaplamasını sağlar.
             */
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.initSlider();
              });
            });
          },
          {
            threshold: 0.01,
            rootMargin: '0px'
          }
        );

      this.visibilityObserver.observe(this);
    }

    initSlider() {
      if (this.slider) return;

      const swiperElement = this.querySelector(
        '.cart-drawer__product-slider-swiper'
      );

      if (
        !swiperElement ||
        typeof Swiper === 'undefined'
      ) {
        return;
      }

      this.slider = new Swiper(swiperElement, {
        slidesPerView: 'auto',
        spaceBetween: 10,

        freeMode: {
          enabled: true,
          momentum: true
        },

        watchOverflow: true,

        /*
         * Swiper'ın görünür slaytlara
         * swiper-slide-visible class'ı vermesini sağlar.
         */
        watchSlidesProgress: true,

        /*
         * Komşu slaytların erkenden yüklenmesini engeller.
         */
        lazyPreloadPrevNext: 0,

        navigation: {
          prevEl: this.querySelector(
            '.cart-drawer__product-slider-nav--prev'
          ),
          nextEl: this.querySelector(
            '.cart-drawer__product-slider-nav--next'
          )
        },

        breakpoints: {
          990: {
            freeMode: {
              enabled: false
            }
          }
        },

        on: {
          init: swiper => {
            this.loadVisibleSlides(swiper);
          },

          slideChange: swiper => {
            this.loadVisibleSlides(swiper);
          },

          transitionEnd: swiper => {
            this.loadVisibleSlides(swiper);
          },

          touchEnd: swiper => {
            this.loadVisibleSlides(swiper);
          },

          resize: swiper => {
            this.loadVisibleSlides(swiper);
          }
        }
      });

      /*
       * Bazı Swiper sürümlerinde init callback'i görünür
       * class'lar eklenmeden çalışabilir.
       */
      requestAnimationFrame(() => {
        this.slider?.update();
        this.loadVisibleSlides(this.slider);
      });
    }

    loadVisibleSlides(swiper = this.slider) {
      if (!swiper) return;

      let visibleSlides = Array.from(
        swiper.slides
      ).filter(slide =>
        slide.classList.contains(
          'swiper-slide-visible'
        )
      );

      /*
       * Görünür class henüz oluşmadıysa aktif slaytı yükle.
       */
      if (
        visibleSlides.length === 0 &&
        swiper.slides[swiper.activeIndex]
      ) {
        visibleSlides = [
          swiper.slides[swiper.activeIndex]
        ];
      }

      visibleSlides.forEach(slide => {
        this.loadPrimaryImage(slide);
      });
    }

    loadPrimaryImage(slide) {
      if (!slide) return;

      const primaryImage = slide.querySelector(
        '.product-card__media img[data-drawer-src], img[data-drawer-src]'
      );

      if (!primaryImage) return;

      /*
       * Zaten görünür olan slaytın ana görseli
       * hemen yüklensin.
       */
      this.loadImage(primaryImage, true);
    }

    loadRemainingImages(slide) {
      if (!slide) return;

      slide
        .querySelectorAll('img[data-drawer-src]')
        .forEach(image => {
          this.loadImage(image, false);
        });
    }

    loadImage(image, eager = false) {
      if (
        !image ||
        !image.hasAttribute('data-drawer-src')
      ) {
        return;
      }

      const sizes = image.getAttribute(
        'data-drawer-sizes'
      );

      const srcset = image.getAttribute(
        'data-drawer-srcset'
      );

      const src = image.getAttribute(
        'data-drawer-src'
      );

      /*
       * <picture> kullanılıyorsa önce source öğeleri.
       */
      const picture = image.closest('picture');

      if (picture) {
        picture
          .querySelectorAll(
            'source[data-drawer-srcset]'
          )
          .forEach(source => {
            const sourceSrcset =
              source.getAttribute(
                'data-drawer-srcset'
              );

            if (sourceSrcset) {
              source.setAttribute(
                'srcset',
                sourceSrcset
              );
            }

            source.removeAttribute(
              'data-drawer-srcset'
            );
          });
      }

      image.loading = eager ? 'eager' : 'lazy';
      image.fetchPriority = 'auto';

      /*
       * Atama sırası önemli:
       * sizes → srcset → src
       */
      if (sizes) {
        image.setAttribute('sizes', sizes);
      }

      if (srcset) {
        image.setAttribute('srcset', srcset);
      }

      if (src) {
        image.setAttribute('src', src);
      }

      image.removeAttribute('data-drawer-sizes');
      image.removeAttribute('data-drawer-srcset');
      image.removeAttribute('data-drawer-src');

      image.classList.remove(
        'product-card__media-image--drawer-deferred'
      );
    }

    /*
     * Hover olduğunda kartın ikinci ve diğer
     * görsellerini yükler.
     */
    handlePointerOver(event) {
      const slide = event.target.closest(
        '.cart-drawer__product-slider-slide'
      );

      if (!slide || !this.contains(slide)) return;

      this.loadRemainingImages(slide);
    }

    /*
     * Klavye kullanıcıları için hover karşılığı.
     */
    handleFocusIn(event) {
      const slide = event.target.closest(
        '.cart-drawer__product-slider-slide'
      );

      if (!slide || !this.contains(slide)) return;

      this.loadRemainingImages(slide);
    }

    destroySlider() {
      if (!this.slider) return;

      this.slider.destroy(true, true);
      this.slider = null;
    }
  }

  customElements.define(
    'cart-drawer-product-slider',
    CartDrawerProductSlider
  );
}